import { useSyncExternalStore } from 'react';
import { api, type ExecutiveViewDto } from '../api/client';

/** `errorSinceMs`: início das falhas seguidas, para a tela do dono calar a falha passageira (796fa1a4). */
type Snapshot = { view: ExecutiveViewDto | null; error: string | null; errorSinceMs: number | null; loading: boolean };
let snapshot: Snapshot = { view: null, error: null, errorSinceMs: null, loading: false };
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | undefined;
let pending: Promise<void> | undefined;
let delay = 30_000;
let signature = '';
export const EXECUTIVE_READ_DEADLINE_MS = 30_000;

function publish(next: Snapshot): void {
  snapshot = next;
  for (const listener of listeners) listener();
}

/** One read for navigation, Hoje and Atenção. Quiet reads back off; an explicit refresh resets the interval. */
export function refreshExecutiveView(): Promise<void> {
  delay = 30_000;
  return load(true);
}

function load(explicit = false): Promise<void> {
  if (pending) return pending;
  clearTimeout(timer);
  publish({ ...snapshot, loading: true });
  const controller = new AbortController();
  let deadline: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    deadline = setTimeout(() => {
      reject(new Error('A leitura demorou mais de 30 segundos. Tente novamente.'));
      controller.abort();
    }, EXECUTIVE_READ_DEADLINE_MS);
  });
  // The race settles even when a transport ignores abort. A late response has no writer into the snapshot.
  pending = Promise.race([api.executiveView(controller.signal), timeout]).then((view) => {
    const nextSignature = JSON.stringify([view.waitingOnOwner, view.source.mapUpdatedAtIso, view.apontamentos.items]);
    delay = nextSignature === signature && !explicit ? Math.min(delay * 2, 120_000) : 30_000;
    signature = nextSignature;
    publish({ view, error: null, errorSinceMs: null, loading: false });
  }).catch((error: unknown) => {
    delay = Math.min(delay * 2, 120_000);
    publish({ ...snapshot, error: error instanceof Error ? error.message : 'falha ao ler', errorSinceMs: snapshot.errorSinceMs ?? Date.now(), loading: false });
  }).finally(() => {
    clearTimeout(deadline);
    pending = undefined;
    if (listeners.size) timer = setTimeout(() => { void load(); }, delay);
  });
  return pending;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) void refreshExecutiveView();
  return () => {
    listeners.delete(listener);
    if (!listeners.size) clearTimeout(timer);
  };
}

export function useExecutiveView(): Snapshot {
  return useSyncExternalStore(subscribe, () => snapshot, () => snapshot);
}
